import { useEffect, useState } from 'react';
import cns from 'classnames';
import { TooltipPropTypes } from '../../../types/GlobalTypes';
import Portal from './Portal';
import S from './Tooltip.module.scss';

/**
 * 툴팁 컴포넌트
 * @description  children: boolean | ReactChild | ReactFragment | ReactPortal | null | undefined;
 *   for: string; // 툴팁 출력 hover 이벤트 대상 ID
 *   direction?: 'top' | 'right' | 'left' | 'bottom'; // 출력 방향(기본값 'top')
 *   top?: number; // top 출력 위치 조정값(기본값 0)
 *   left?: number; // left 출력 위치 조정값(기본값 0)
 *   zIndex?: number; // z-index(기본값 500)
 * @constructor
 * @param props
 */
const Tooltip = (props: TooltipPropTypes) => {
  const {
    variant,
    leftSlot,
    rightSlot,
    direction,
    showEventType,
    hideEventType,
    top,
    left,
    zIndex,
    children
  } = props;
  const [mounted, setMounted] = useState(false); // 마운트 된 후에 엘리먼트를 그리도록 설정하기 위한 State
  const [isHidden, setIsHidden] = useState(true); // 툴팁 노출 여부 State
  const [position, setPosition] = useState<{
    top: string | number;
    left: string | number;
  }>({
    top: 0,
    left: 0
  }); // 툴팁 위치 State
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null); // 툴팁 위치 State

  const repositionLeft = left;
  const repositionTop = top;
  /**
   * 툴팁 출력 위치 설정
   * @param targetElement
   */
  function setTooltipPosition(targetElement: Element | null) {
    if (targetElement) {
      const targetClientRect = targetElement.getBoundingClientRect();
      let top: string | number = 0;
      let left: string | number = 0;
      if (!direction || direction === 'top') {
        top = targetClientRect.top + (repositionTop || -5);
        left = targetClientRect.left + targetClientRect.width / 2 + (repositionLeft || 0);
      } else if (direction === 'right') {
        top = targetClientRect.top + targetClientRect.height / 2 + (repositionTop || 0);
        left = targetClientRect.right + (repositionLeft || 5);
      } else if (direction === 'bottom') {
        top = targetClientRect.bottom + (repositionTop || 5);
        left = targetClientRect.left + targetClientRect.width / 2 + (repositionLeft || 0);
      } else if (direction === 'left') {
        top = targetClientRect.top + targetClientRect.height / 2 + (repositionTop || 0);
        left = targetClientRect.left + (repositionLeft || -5);
      }
      setPosition({
        top: top === 0 ? 0 : `${top}px`,
        left: left === 0 ? 0 : `${left}px`
      });
    }
  }

  /**
   * 툴팁 노출 이벤트 액션
   */
  const showTooltip = () => {
    if (isHidden) {
      setTooltipPosition(targetElement);
      targetElement?.addEventListener(hideEventType || 'mouseout', hideTooltip);
      if (!hideEventType || hideEventType === 'mouseout') {
        document.addEventListener('scroll', hideTooltip, true);
      }
      setIsHidden(false);
    }
  };

  /**
   * 툴팁 숨기기 이벤트 액션
   */
  const hideTooltip = () => {
    targetElement?.removeEventListener(hideEventType || 'mouseout', hideTooltip);
    if (!hideEventType || hideEventType === 'mouseout') {
      document.removeEventListener('scroll', hideTooltip, true);
    }
    setIsHidden(true);
  };

  /**
   * mounted 또는 툴팁 위치 변경 시 수행
   */
  useEffect(() => {
    setMounted(true);
    setTargetElement(document.getElementById(props.for));
    return () => {
      setMounted(false);
    };
  }, []);
  useEffect(() => {
    setTooltipPosition(targetElement);
    targetElement?.addEventListener(showEventType, showTooltip);
    return () => {
      targetElement?.removeEventListener(showEventType, showTooltip);
      targetElement?.removeEventListener(hideEventType || 'mouseout', hideTooltip);
      document.removeEventListener('scroll', hideTooltip, true);
    };
  }, [targetElement, direction, top, left]);
  return <>
      {mounted && <Portal targetId="tooltip">
          <div className={cns(S.tooltip, S[variant], S[direction || 'top'], {
        hidden: isHidden
      })} style={{
        top: position.top,
        left: position.left,
        zIndex: zIndex || 500,
        width: props.width
      }}>
            {leftSlot && <span className={S.slot}>{leftSlot}</span>}
            {children}
            {rightSlot && <span className={S.slot}>{rightSlot}</span>}
          </div>
        </Portal> || null}
    </>;
};
export default Tooltip;